import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Row, Col, Form } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import { EventTypes } from '@awp/constants';
import {
  isJsonValueEmpty,
  isInt,
  isValueEmpty,
  getUtcToLocalByFormat,
  containsSpecialChars,
} from '@awp/helpers/utilsReact';
import { isValid, setHours, setMinutes, subDays, parseISO } from 'date-fns';
import {
  getAvalancheGeographicAreas,
  getAvalancheGeographicPaths,
  selectGeographicAreaLinesDd,
  selectGeographicAreaPointsDd,
} from '@awp/stores/avalancheSlice';
import { selectCurrentUser } from '@awp/stores/usersSlice';
import { selectAvalancheTechnicians } from '@awp/stores/dropdownsSlice';
import {
  criticalFieldEntity,
  criticalFieldsToEntity,
  checkEventType,
  findEventTypeNmById,
  getTimeLabel,
  isPathEvent,
  getSegmentId,
  getFromId,
  getToId,
} from './ProcessedCriticalFieldsHelper';
import { json } from 'react-router-dom';

const txtFontSize = {
  fontSize: '85%',
};

const ProcessedCriticalFieldsEdit = ({
  key,
  dto,
  updatedDto,
  criticalFields,
  setCriticalFields,
  eventPaths,
  updateEventPath,
  getEventPathValue,
  eventSegment,
  setEventSegment,
  eventFrom,
  setEventFrom,
  eventTo,
  setEventTo,
  isParent,
  eventLocation,
  updateEventLocation,
  getEventLocationValue,
}) => {
  const dispatch = useDispatch();
  const avalanchePrograms = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE PROGRAM'));
  const avalancheAreas = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE AREA'));
  const avalancheTechs = useSelector((state) => selectAvalancheTechnicians(state));
  const areaLines = useSelector((state) => selectGeographicAreaLinesDd(state));
  const areaPoints = useSelector((state) => selectGeographicAreaPointsDd(state));
  const avalanchePaths = useSelector((state) => getAvalancheGeographicPaths(state));
  const updatePathOption = (e, id) => {
    if (!e) return;
    updateEventPath(id, e);
    let _criticalFields = { ...criticalFields };
    if (_criticalFields && _criticalFields.ChildAvalancheProcessedEvents) {
      let _children = [..._criticalFields.ChildAvalancheProcessedEvents];
      if (Array.isArray(_children)) {
        let _updatedChildren = [];
        _children.map((c) => {
          let _c = { ...c };
          if (_c && _c.ChildAvalancheProcessedEvents) {
            let _gChildren = [..._c.ChildAvalancheProcessedEvents];
            let _updatedGChildren = [];
            _gChildren.map((gc) => {
              let _gc = { ...gc };
              if (_gc.EventId === id && checkEventType(_gc.EventTypeNm)) {
                _gc.GeographicAreaId = e.areaId; //path id
                _gc.ParentGeographicAreaId = e.parentId; //avalanche area id
              }
              _updatedGChildren.push(_gc);
            });
            _c.ChildAvalancheProcessedEvents = _updatedGChildren;
          }
          if (_c.EventId === id && checkEventType(c.EventTypeNm)) {
            _c.GeographicAreaId = e.areaId; //path id
            _c.ParentGeographicAreaId = e.parentId; //avalanche area id
          }
          _updatedChildren.push(_c);
        });
        _criticalFields.ChildAvalancheProcessedEvents = _updatedChildren;
      }
      if (_criticalFields.EventId === id && checkEventType(_criticalFields.EventTypeNm)) {
        _criticalFields.GeographicAreaId = e.areaId; //path id
        _criticalFields.ParentGeographicAreaId = e.parentId; //avalanche area id
      }
      setCriticalFields(_criticalFields);
    }
  };

  const updateLocationIds = (evt, value, _type) => {
    let geographicPointIds = evt.GeographicPointIds
      ? evt.GeographicPointIds.split(',').map((id) => (id ? Number(id) : null))
      : [null, null]; // Default to an array with two null values if null
    switch (_type) {
      case 'from':
        geographicPointIds[0] = value; // Update the "from" value
        evt.GeographicPointIds = geographicPointIds.map((id) => (id !== null ? id : '')).join(',');
        evt.GeographicLineId = null;
        break;
      case 'to':
        geographicPointIds[1] = value; // Update the "to" value
        evt.GeographicPointIds = geographicPointIds.map((id) => (id !== null ? id : '')).join(',');
        evt.GeographicLineId = null;
        break;
      case 'segment':
        evt.GeographicLineId = value; // Update GeographicLineId for "segment"
        evt.GeographicPointIds = null;
        break;
      default:
        console.error("Invalid type. Must be 'from', 'to', or 'segment'.");
        break;
    }

    return evt;
  };
  const updateLocationOption = (e, id, _type) => {
    if (!e) return;
    updateEventLocation(id, e, _type);
    let _criticalFields = { ...criticalFields };
    if (_criticalFields && _criticalFields.ChildAvalancheProcessedEvents) {
      let _children = [..._criticalFields.ChildAvalancheProcessedEvents];
      if (Array.isArray(_children)) {
        let _updatedChildren = [];
        _children.map((c) => {
          let _c = { ...c };
          if (_c && _c.ChildAvalancheProcessedEvents) {
            let _gChildren = [..._c.ChildAvalancheProcessedEvents];
            let _updatedGChildren = [];
            _gChildren.map((gc) => {
              let _gc = { ...gc };
              if (!checkEventType(_gc.EventTypeNm)) {
                _gc.GeographicAreaId = e.areaId; //path id
                _gc.ParentGeographicAreaId = e.parentId; //avalanche area id
                updateLocationIds(_gc, e.value, _type);
              }
              _updatedGChildren.push(_gc);
            });
            _c.ChildAvalancheProcessedEvents = _updatedGChildren;
          }
          if (!checkEventType(c.EventTypeNm)) {
            _c.GeographicAreaId = e.areaId; //path id
            _c.ParentGeographicAreaId = e.parentId; //avalanche area id
            updateLocationIds(_c, e.value, _type);
          }
          _updatedChildren.push(_c);
        });
        _criticalFields.ChildAvalancheProcessedEvents = _updatedChildren;
      }
      if (!checkEventType(_criticalFields.EventTypeNm)) {
        _criticalFields.GeographicAreaId = e.areaId; //path id
        _criticalFields.ParentGeographicAreaId = e.parentId; //avalanche area id
        updateLocationIds(_criticalFields, e.value, _type);
      }
      setCriticalFields(_criticalFields);
    }
  };
  const getEffectiveTime = (cf, updatedCf) => {
    if (!cf || !updatedCf) return '';

    if (cf.EventId === updatedCf.eventId) {
      if (!updatedCf.timestamp) return '';
      return updatedCf.timestamp;
    }
    if (!cf.EventTimestamp) return '';
    const dt = (cf.EventTimestamp + '').endsWith('Z') ? cf.EventTimestamp : cf.EventTimestamp + 'Z';
    return new Date(dt);
  };
  return (
    <>
      <div style={{ border: '0.063rem solid #c0c0c0', paddingLeft: '15px' }}>
        <Row>
          <div style={{ paddingLeft: '0.5rem', paddingTop: '0.5rem' }}>{dto.EventTypeDisp}</div>
        </Row>
        <Row>
          {dto && checkEventType(dto.EventTypeNm) && updatedDto && !updatedDto.avalanche_path && (
            <Col lg={3} md={3} sm={8}>
              <Form.Group>
                <Form.Label className="flex-grow-1" htmlFor={key}>
                  <b>{'Path'}</b>
                </Form.Label>
                <Select
                  id={'processed_cf_' + dto.EventId + '_fp_path'}
                  options={avalanchePaths.filter((x) => updatedDto.area && x.parentId === updatedDto.area.value)}
                  //isClearable={true}
                  value={getEventPathValue(dto.EventId)}
                  placeholder={'Select ...'}
                  onChange={(e) => updatePathOption(e, dto.EventId)}
                />
                {!getEventPathValue(dto.EventId) && (
                  <Form.Control.Feedback type="invalid" className="d-block" style={{ fontSize: '100%' }}>
                    <li key={1}>{'Path is required'}</li>
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          )}
          {dto && checkEventType(dto.EventTypeNm) && updatedDto && isPathEvent(updatedDto) && (
            <Col lg={3} md={3} sm={8}>
              <Form.Group>
                <Form.Label className="flex-grow-1" htmlFor={key}>
                  <b>{'Path'}</b>
                </Form.Label>
                <Select
                  id={'processed_cf_' + dto.EventId + '_fp_path'}
                  options={avalanchePaths.filter(
                    (x) => updatedDto.avalanche_area && x.parentId === updatedDto.avalanche_area.value,
                  )}
                  isDisabled={dto.EventId === updatedDto.eventId}
                  value={
                    dto.EventId === updatedDto.eventId
                      ? avalanchePaths.find(
                          (x) => updatedDto.avalanche_path && x.areaId === updatedDto.avalanche_path.areaId,
                        )
                      : getEventPathValue(dto.EventId)
                  }
                  placeholder={'Select ...'}
                  onChange={(e) => updatePathOption(e, dto.EventId)}
                />
                {dto.EventId !== updatedDto.eventId && !getEventPathValue(dto.EventId) && (
                  <Form.Control.Feedback type="invalid" className="d-block" style={{ fontSize: '100%' }}>
                    <li key={1}>{'Path is required'}</li>
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          )}
          {dto && !checkEventType(dto.EventTypeNm) && updatedDto && !isPathEvent(updatedDto) && (
            <Col lg={3} md={3} sm={8}>
              <Form.Label className="flex-grow-1" htmlFor={key}>
                <b>{'Road Closure Area'}</b>
              </Form.Label>
              <Select
                id={'processed_cf_' + dto.EventId + '_fp_segment'}
                options={areaLines.filter(
                  (x) => updatedDto.segment && updatedDto.segment.areaId > 0 && x.areaId === updatedDto.segment.areaId,
                )}
                //isClearable={true}
                value={
                  !updatedDto.segment ||
                  !updatedDto.segment.value ||
                  areaLines.find((x) => updatedDto.segment.value > 0 && x.value === updatedDto.segment.value)
                }
                isDisabled={
                  (updatedDto.segment && updatedDto.segment.value) ||
                  (updatedDto.fromPoint && updatedDto.fromPoint.value) ||
                  (updatedDto.toPoint && updatedDto.toPoint.value)
                }
                placeholder={'Select ...'}
                onChange={updatePathOption}
              />
            </Col>
          )}
          {dto && !checkEventType(dto.EventTypeNm) && updatedDto && isPathEvent(updatedDto) && (
            <Col lg={3} md={3} sm={8}>
              <Form.Group>
                <Form.Label className="flex-grow-1" htmlFor={key}>
                  <b>{'Road Closure Area'}</b>
                </Form.Label>
                <Select
                  id={'processed_cf_' + dto.EventId + '_fp_segment_loc'}
                  options={areaLines.filter(
                    (x) =>
                      updatedDto.avalanche_area &&
                      updatedDto.avalanche_area.value > 0 &&
                      x.areaId === updatedDto.avalanche_area.value,
                  )}
                  value={getEventLocationValue(dto.EventId, 'segment')}
                  placeholder={'Select ...'}
                  onChange={(e) => updateLocationOption(e, dto.EventId, 'segment')}
                />
                {!getEventLocationValue(dto.EventId, 'segment') &&
                  !getEventLocationValue(dto.EventId, 'from') &&
                  !getEventLocationValue(dto.EventId, 'to') && (
                    <Form.Control.Feedback type="invalid" className="d-block" style={{ fontSize: '100%' }}>
                      <li key={1}>{'Either Road Closure Area or From/To is required'}</li>
                    </Form.Control.Feedback>
                  )}
              </Form.Group>
            </Col>
          )}
          {dto && !checkEventType(dto.EventTypeNm) && updatedDto && !isPathEvent(updatedDto) && (
            <Col lg={3} md={3} sm={8}>
              <Form.Group>
                <Form.Label className="flex-grow-1" htmlFor={key}>
                  <b>{'From'}</b>
                </Form.Label>
                <Select
                  id={'processed_cf_' + dto.EventId + '_fp_fromPoint'}
                  options={areaPoints.filter((x) => updatedDto.fromPoint && x.areaId === updatedDto.fromPoint.areaId)}
                  //isClearable={true}
                  value={
                    !updatedDto.fromPoint ||
                    !updatedDto.fromPoint.value ||
                    areaPoints.find((x) => x.value === updatedDto.fromPoint.value)
                  }
                  isDisabled={
                    (updatedDto.segment && updatedDto.segment.value) ||
                    (updatedDto.fromPoint && updatedDto.fromPoint.value) ||
                    (updatedDto.toPoint && updatedDto.toPoint.value)
                  }
                  placeholder={'Select ...'}
                  onChange={updatePathOption}
                />
                {/* {!isValueEmpty(getError('PlacementSegment.SourceName')) && (
                  <Form.Control.Feedback type="invalid" className="d-block">
                    {getError('PlacementSegment.SourceName')}
                  </Form.Control.Feedback>
                )} */}
              </Form.Group>
            </Col>
          )}
          {dto && !checkEventType(dto.EventTypeNm) && updatedDto && !isPathEvent(updatedDto) && (
            <Col lg={3} md={3} sm={8}>
              <Form.Label className="flex-grow-1">
                <b>{'To'}</b>
              </Form.Label>
              <Select
                id={'processed_cf_' + dto.EventId + '_tp_toPoint'}
                options={areaPoints.filter((x) => updatedDto.toPoint && x.areaId === updatedDto.toPoint.areaId)}
                //isClearable={true}
                value={
                  !updatedDto.toPoint ||
                  !updatedDto.toPoint.value ||
                  areaPoints.find((x) => x.value === updatedDto.toPoint.value)
                }
                isDisabled={
                  (updatedDto.segment && updatedDto.segment.value) ||
                  (updatedDto.fromPoint && updatedDto.fromPoint.value) ||
                  (updatedDto.toPoint && updatedDto.toPoint.value)
                }
                placeholder={'Select ...'}
                onChange={updatePathOption}
              />
            </Col>
          )}
          {dto && !checkEventType(dto.EventTypeNm) && updatedDto && isPathEvent(updatedDto) && (
            <Col lg={3} md={3} sm={8}>
              <Form.Group>
                <Form.Label className="flex-grow-1" htmlFor={key}>
                  <b>{'From'}</b>
                </Form.Label>
                <Select
                  id={'processed_cf_' + dto.EventId + '_fp_fromPoint_loc'}
                  options={areaPoints.filter(
                    (x) =>
                      updatedDto.avalanche_area &&
                      x.areaId === updatedDto.avalanche_area.value &&
                      (!getEventLocationValue(dto.EventId, 'to') ||
                        (getEventLocationValue(dto.EventId, 'to') &&
                          x.value !== getEventLocationValue(dto.EventId, 'to').value)),
                  )}
                  value={getEventLocationValue(dto.EventId, 'from')}
                  // isDisabled={
                  //   (updatedDto.segment && updatedDto.segment.value) ||
                  //   (updatedDto.fromPoint && updatedDto.fromPoint.value) ||
                  //   (updatedDto.toPoint && updatedDto.toPoint.value)
                  // }
                  placeholder={'Select ...'}
                  onChange={(e) => updateLocationOption(e, dto.EventId, 'from')}
                />
                {!getEventLocationValue(dto.EventId, 'segment') &&
                  !getEventLocationValue(dto.EventId, 'from') &&
                  !getEventLocationValue(dto.EventId, 'to') && (
                    <Form.Control.Feedback type="invalid" className="d-block" style={{ fontSize: '100%' }}>
                      <li key={1}>{'Either Road Closure Area or From/To is required'}</li>
                    </Form.Control.Feedback>
                  )}
              </Form.Group>
            </Col>
          )}
          {dto && !checkEventType(dto.EventTypeNm) && updatedDto && isPathEvent(updatedDto) && (
            <Col lg={3} md={3} sm={8}>
              <Form.Group>
                <Form.Label className="flex-grow-1">
                  <b>{'To'}</b>
                </Form.Label>
                <Select
                  id={'processed_cf_' + dto.EventId + '_tp_toPoint_loc'}
                  options={areaPoints.filter(
                    (x) =>
                      updatedDto.avalanche_area &&
                      x.areaId === updatedDto.avalanche_area.value &&
                      (!getEventLocationValue(dto.EventId, 'from') ||
                        (getEventLocationValue(dto.EventId, 'from') &&
                          x.value !== getEventLocationValue(dto.EventId, 'from').value)),
                  )}
                  value={getEventLocationValue(dto.EventId, 'to')}
                  // isDisabled={
                  //   (updatedDto.segment && updatedDto.segment.value) ||
                  //   (updatedDto.fromPoint && updatedDto.fromPoint.value) ||
                  //   (updatedDto.toPoint && updatedDto.toPoint.value)
                  // }
                  placeholder={'Select ...'}
                  onChange={(e) => updateLocationOption(e, dto.EventId, 'to')}
                />
                {!getEventLocationValue(dto.EventId, 'segment') &&
                  !getEventLocationValue(dto.EventId, 'from') &&
                  !getEventLocationValue(dto.EventId, 'to') && (
                    <Form.Control.Feedback type="invalid" className="d-block" style={{ fontSize: '100%' }}>
                      <li key={1}>{'Either Road Closure Area or From/To is required'}</li>
                    </Form.Control.Feedback>
                  )}
              </Form.Group>
            </Col>
          )}
          <Col lg={3} md={3} sm={8}>
            {dto && updatedDto && (
              <div style={{ marginRight: '1rem' }}>
                <Form.Label className="flex-grow-1">
                  <b>{getTimeLabel(dto.EventTypeNm)}</b>
                </Form.Label>
                <div className="d-flex">
                  <DatePicker
                    selected={getEffectiveTime(dto, updatedDto)}
                    className="form-control"
                    dateFormat={['yyyy-MM-dd HH:mm', 'yyyy-MM-dd', 'yyyyMMdd', 'yyyyMMddHHmm']}
                    showTimeSelect
                    closeOnSelect={true}
                    timeFormat="HH:mm"
                    timeIntervals={60}
                    disabled={true}
                    onChange={(date) => {}}
                  />
                </div>
              </div>
            )}
          </Col>
        </Row>
        <Row style={{ height: '1rem' }}></Row>
        {!isParent &&
          dto &&
          dto.ChildAvalancheProcessedEvents &&
          Object.keys(dto.ChildAvalancheProcessedEvents).map((d, idx) => {
            let _dto = dto.ChildAvalancheProcessedEvents[idx];
            return (
              <div key={idx} style={{ paddingLeft: '15px' }}>
                <Row>
                  <div>{_dto.EventTypeDisp}</div>
                </Row>
                <Row>
                  {_dto && checkEventType(_dto.EventTypeNm) && updatedDto && !updatedDto.avalanche_path && (
                    <Col lg={3} md={3} sm={8}>
                      <Form.Group>
                        <Form.Label className="flex-grow-1" htmlFor={key}>
                          <b>{'Path'}</b>
                        </Form.Label>
                        <Select
                          id={'processed_cf_child_' + _dto.EventId + '_fp_path'}
                          options={avalanchePaths.filter(
                            (x) => updatedDto.area && x.parentId === updatedDto.area.value,
                          )}
                          //isClearable={true}
                          value={getEventPathValue(_dto.EventId)}
                          placeholder={'Select ...'}
                          onChange={(e) => updatePathOption(e, _dto.EventId)}
                        />
                        {!getEventPathValue(_dto.EventId) && (
                          <Form.Control.Feedback type="invalid" className="d-block" style={{ fontSize: '100%' }}>
                            <li key={1}>{'Path is required'}</li>
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                  )}
                  {_dto && checkEventType(_dto.EventTypeNm) && updatedDto && isPathEvent(updatedDto) && (
                    <Col lg={3} md={3} sm={8}>
                      <Form.Group>
                        <Form.Label className="flex-grow-1" htmlFor={key}>
                          <b>{'Path'}</b>
                        </Form.Label>
                        <Select
                          id={'processed_cf_child_' + _dto.EventId + '_fp_path'}
                          options={avalanchePaths.filter(
                            (x) => updatedDto.avalanche_area && x.parentId === updatedDto.avalanche_area.value,
                          )}
                          isDisabled={_dto.EventId === updatedDto.eventId}
                          value={
                            _dto.EventId === updatedDto.eventId
                              ? avalanchePaths.find(
                                  (x) => updatedDto.avalanche_path && x.areaId === updatedDto.avalanche_path.areaId,
                                )
                              : getEventPathValue(_dto.EventId)
                          }
                          placeholder={'Select ...'}
                          onChange={(e) => updatePathOption(e, _dto.EventId)}
                        />
                        {_dto.EventId !== updatedDto.eventId && !getEventPathValue(_dto.EventId) && (
                          <Form.Control.Feedback type="invalid" className="d-block" style={{ fontSize: '100%' }}>
                            <li key={1}>{'Path is required'}</li>
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                  )}
                  {_dto && !checkEventType(_dto.EventTypeNm) && updatedDto && (
                    <Col lg={3} md={3} sm={8}>
                      <Form.Label className="flex-grow-1" htmlFor={key}>
                        <b>{'Road Closure Area'}</b>
                      </Form.Label>
                      <Select
                        id={'processed_cf_child_' + _dto.EventId + '_fp_segment'}
                        options={areaLines.filter(
                          (x) =>
                            updatedDto.segment &&
                            updatedDto.segment.areaId > 0 &&
                            x.areaId === updatedDto.segment.areaId,
                        )}
                        isClearable={true}
                        value={
                          !updatedDto.segment ||
                          !updatedDto.segment.value ||
                          areaLines.find((x) => updatedDto.segment.value > 0 && x.value === updatedDto.segment.value)
                        }
                        isDisabled={
                          (updatedDto.segment && updatedDto.segment.value) ||
                          (updatedDto.fromPoint && updatedDto.fromPoint.value) ||
                          (updatedDto.toPoint && updatedDto.toPoint.value)
                        }
                        placeholder={'Select ...'}
                        onChange={updatePathOption}
                      />
                    </Col>
                  )}
                  {_dto && !checkEventType(_dto.EventTypeNm) && updatedDto && (
                    <Col lg={3} md={3} sm={8}>
                      <Form.Label className="flex-grow-1" htmlFor={key}>
                        <b>{'From'}</b>
                      </Form.Label>
                      <Select
                        id={'processed_cf_child_' + _dto.EventId + '_fp_fromPoint'}
                        options={areaPoints.filter(
                          (x) => updatedDto.fromPoint && x.areaId === updatedDto.fromPoint.areaId,
                        )}
                        //isClearable={true}
                        value={
                          !updatedDto.fromPoint ||
                          !updatedDto.fromPoint.value ||
                          areaPoints.find((x) => x.value === updatedDto.fromPoint.value)
                        }
                        isDisabled={
                          (updatedDto.segment && updatedDto.segment.value) ||
                          (updatedDto.fromPoint && updatedDto.fromPoint.value) ||
                          (updatedDto.toPoint && updatedDto.toPoint.value)
                        }
                        placeholder={'Select ...'}
                        onChange={updatePathOption}
                      />
                    </Col>
                  )}
                  {_dto && !checkEventType(_dto.EventTypeNm) && updatedDto && (
                    <Col lg={3} md={3} sm={8}>
                      <Form.Label className="flex-grow-1">
                        <b>{'To'}</b>
                      </Form.Label>
                      <Select
                        id={'processed_cf_child_' + _dto.EventId + '_tp_toPoint'}
                        options={areaPoints.filter((x) => updatedDto.toPoint && x.areaId === updatedDto.toPoint.areaId)}
                        //isClearable={true}
                        value={
                          !updatedDto.toPoint ||
                          !updatedDto.toPoint.value ||
                          areaPoints.find((x) => x.value === updatedDto.toPoint.value)
                        }
                        isDisabled={
                          (updatedDto.segment && updatedDto.segment.value) ||
                          (updatedDto.fromPoint && updatedDto.fromPoint.value) ||
                          (updatedDto.toPoint && updatedDto.toPoint.value)
                        }
                        placeholder={'Select ...'}
                        onChange={updatePathOption}
                      />
                    </Col>
                  )}
                  <Col lg={3} md={3} sm={8}>
                    {_dto && (
                      <>
                        <Form.Label className="flex-grow-1">
                          <b>{getTimeLabel(_dto.EventTypeNm)}</b>
                        </Form.Label>
                        <div className="d-flex">
                          <DatePicker
                            selected={getEffectiveTime(_dto, updatedDto)}
                            className="form-control"
                            dateFormat={['yyyy-MM-dd HH:mm', 'yyyy-MM-dd', 'yyyyMMdd', 'yyyyMMddHHmm']}
                            showTimeSelect
                            closeOnSelect={true}
                            timeFormat="HH:mm"
                            timeIntervals={60}
                            disabled={true}
                            onChange={(date) => {
                              // if (isValid(date) || isValueEmpty(date)) {
                              //   let _selectedSegment = { ...selectedSegment };
                              //   _selectedSegment.SegmentEndTimestamp = isValueEmpty(date) ? '' : strDateTime(date);
                              //   dispatch(setSegment(_selectedSegment));
                              // }
                            }}
                          />
                        </div>
                      </>
                    )}
                  </Col>
                </Row>
                <Row style={{ height: '1rem' }}></Row>
              </div>
            );
          })}
      </div>
    </>
  );
};

// ProcessedCriticalFieldsClosurePhaseEvtEdit.propTypes = {
//   //segment: PropTypes.any.isRequired,
// };

export default ProcessedCriticalFieldsEdit;
